import React, { useState, useEffect, useMemo, memo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { PhoneCall, CheckCircle, AlertCircle, Users, Search } from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

// Alineados con backend/models.py::GestionCobranzaTipo/Canal/Resultado
const tiposGestion = [
  { value: 'llamada', label: 'Llamada telefónica' },
  { value: 'correo_enviado', label: 'Correo enviado' },
  { value: 'correo_recibido', label: 'Correo recibido' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'reunion', label: 'Reunión presencial' },
  { value: 'recordatorio', label: 'Recordatorio' },
  { value: 'seguimiento_interno', label: 'Seguimiento interno' },
  { value: 'derivacion', label: 'Derivación' },
  { value: 'otra', label: 'Otra' },
];

const canalesGestion = [
  { value: 'telefono', label: 'Teléfono' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'videollamada', label: 'Videollamada' },
  { value: 'interno', label: 'Interno' },
];

const resultadosGestion = [
  { value: 'sin_respuesta', label: 'Sin respuesta' },
  { value: 'cliente_informado', label: 'Cliente informado' },
  { value: 'pago_prometido', label: 'Pago prometido' },
  { value: 'pago_parcial_prometido', label: 'Pago parcial prometido' },
  { value: 'cliente_objeta_saldo', label: 'Cliente objeta saldo' },
  { value: 'cliente_solicita_plazo', label: 'Cliente solicita plazo' },
  { value: 'pago_recibido', label: 'Pago recibido' },
  { value: 'derivar_revision', label: 'Derivar a revisión' },
  { value: 'derivar_judicial', label: 'Derivar a judicial' },
  { value: 'cerrado', label: 'Cerrado' },
];

// Resultados/tipos donde el contacto es OBLIGATORIO (regla del backend)
const RESULTADOS_QUE_REQUIEREN_CONTACTO = new Set([
  'cliente_informado', 'pago_prometido', 'pago_parcial_prometido',
  'cliente_objeta_saldo', 'cliente_solicita_plazo', 'pago_recibido'
]);
// Tipos/canales donde el contacto NUNCA es requerido (aunque el resultado lo pida)
const TIPOS_INTERNOS = new Set(['seguimiento_interno', 'derivacion']);
const CANAL_INTERNO = 'interno';

function requiereContacto(tipo, canal, resultado) {
  if (canal === CANAL_INTERNO) return false;
  if (TIPOS_INTERNOS.has(tipo)) return false;
  if (resultado === 'sin_respuesta') return false;
  if (tipo === 'correo_enviado') return true;
  return RESULTADOS_QUE_REQUIEREN_CONTACTO.has(resultado);
}

/**
 * Modal para crear una gestión de cobranza usando el RUT como unidad principal.
 * Props:
 *  - open, onOpenChange
 *  - cuenta: objeto de cuenta consolidada (rut_normalizado, nombre_principal, principal_client_id, client_ids)
 *  - onSuccess: callback tras crear la gestión
 */
const NuevaGestionModal = memo(({ open, onOpenChange, cuenta, onSuccess }) => {
  const [form, setForm] = useState({
    tipo_gestion: 'llamada',
    canal: 'telefono',
    resultado: 'sin_respuesta',
    resumen: '',
    factura_id: '',
    contacto_idx: '',
    proxima_accion: '',
    fecha_proxima_gestion: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [facturaFilter, setFacturaFilter] = useState('');

  const contactoRequerido = useMemo(
    () => requiereContacto(form.tipo_gestion, form.canal, form.resultado),
    [form.tipo_gestion, form.canal, form.resultado]
  );

  // Cargar contactos + facturas de la cuenta al abrir el modal
  useEffect(() => {
    if (!open || !cuenta?.rut_normalizado) return;
    setLoadingDetalle(true);
    setContactos([]);
    setFacturas([]);
    setForm(f => ({ ...f, contacto_idx: '', factura_id: '' }));
    api.get(`/finanzas/cobranza/cuentas-consolidadas/${encodeURIComponent(cuenta.rut_normalizado)}/detalle-completo`)
      .then(r => {
        setContactos(r.data?.contactos || []);
        // Solo facturas con saldo pendiente
        const pendientes = (r.data?.facturas || []).filter(f => (f.status !== 'pagada'));
        setFacturas(pendientes);
      })
      .catch(err => toast.error(formatErrorMessage(err) || 'Error al cargar detalles de la cuenta'))
      .finally(() => setLoadingDetalle(false));
  }, [open, cuenta]);

  const facturasFiltradas = useMemo(() => {
    const q = facturaFilter.trim().toLowerCase();
    if (!q) return facturas;
    return facturas.filter(f =>
      (f.numero_factura || '').toLowerCase().includes(q)
      || (f.id || '').toLowerCase().includes(q)
    );
  }, [facturas, facturaFilter]);

  const handleSubmit = async () => {
    if (!cuenta?.principal_client_id) {
      toast.error('Cuenta sin cliente principal.');
      return;
    }
    if (!form.resumen.trim()) {
      toast.error('El resumen es obligatorio.');
      return;
    }
    let contacto_usado = null;
    if (form.contacto_idx !== '' && form.contacto_idx !== 'ninguno') {
      const c = contactos[Number(form.contacto_idx)];
      if (c) {
        contacto_usado = {
          nombre: c.nombre, email: c.email, telefono: c.telefono,
          rol: c.rol, origen: c.origen, client_id: c.client_id,
        };
      }
    }
    if (contactoRequerido && !contacto_usado) {
      toast.error('Este tipo/resultado exige seleccionar un contacto.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        cliente_id: cuenta.principal_client_id,
        cliente_nombre: cuenta.nombre_principal,
        tipo_gestion: form.tipo_gestion,
        canal: form.canal,
        resultado: form.resultado || null,
        resumen: form.resumen.trim(),
        factura_id: form.factura_id || null,
        contacto_usado,
        proxima_accion: form.proxima_accion || null,
        fecha_proxima_gestion: form.fecha_proxima_gestion || null,
      };
      const r = await api.post('/finanzas/cobranza/gestiones', body);
      toast.success('Gestión registrada correctamente');
      onOpenChange(false);
      onSuccess?.(r.data?.gestion);
    } catch (err) {
      toast.error(formatErrorMessage(err) || 'Error al registrar gestión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto' data-testid='nueva-gestion-modal'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <PhoneCall className='w-5 h-5 text-blue-600' />
            Nueva Gestión de Cobranza
          </DialogTitle>
          <DialogDescription>
            {cuenta ? <>Cuenta consolidada por RUT <span className='font-mono'>{cuenta.rut_display || cuenta.rut_normalizado}</span> · {cuenta.nombre_principal}</> : 'Seleccione una cuenta primero.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='grid grid-cols-3 gap-3'>
            <div>
              <Label className='text-xs'>Tipo de gestión</Label>
              <Select value={form.tipo_gestion} onValueChange={v => setForm(f => ({ ...f, tipo_gestion: v }))}>
                <SelectTrigger data-testid='gestion-tipo' className='h-9'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tiposGestion.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className='text-xs'>Canal</Label>
              <Select value={form.canal} onValueChange={v => setForm(f => ({ ...f, canal: v }))}>
                <SelectTrigger data-testid='gestion-canal' className='h-9'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {canalesGestion.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className='text-xs'>Resultado</Label>
              <Select value={form.resultado} onValueChange={v => setForm(f => ({ ...f, resultado: v }))}>
                <SelectTrigger data-testid='gestion-resultado' className='h-9'><SelectValue /></SelectTrigger>
                <SelectContent>
                  {resultadosGestion.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className='text-xs'>Resumen *</Label>
            <DebouncedTextarea
              value={form.resumen}
              onChange={e => setForm(f => ({ ...f, resumen: e.target.value }))}
              placeholder='Detalles de la gestión'
              rows={3}
              data-testid='gestion-resumen'
            />
          </div>

          {/* Factura opcional */}
          <div>
            <Label className='text-xs flex items-center gap-2'>
              Factura de referencia <span className='text-slate-400'>(opcional)</span>
            </Label>
            <div className='space-y-1'>
              <div className='flex gap-2'>
                <Input
                  placeholder='Buscar por número...'
                  value={facturaFilter}
                  onChange={e => setFacturaFilter(e.target.value)}
                  className='h-8 text-xs flex-1'
                  data-testid='gestion-factura-filter'
                />
                <Select
                  value={form.factura_id || 'ninguna'}
                  onValueChange={v => setForm(f => ({ ...f, factura_id: v === 'ninguna' ? '' : v }))}
                >
                  <SelectTrigger data-testid='gestion-factura' className='h-8 text-xs w-64'>
                    <SelectValue placeholder={loadingDetalle ? 'Cargando...' : 'Sin factura (deuda total)'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='ninguna'>Sin factura (gestión general)</SelectItem>
                    {facturasFiltradas.slice(0, 100).map(f => (
                      <SelectItem key={f.id} value={f.id}>#{f.numero_factura} · ${new Intl.NumberFormat('es-CL').format(f.total_amount_clp || 0)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contacto */}
          <div className={`p-3 rounded-lg border ${contactoRequerido ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
            <div className='flex items-center justify-between mb-2'>
              <Label className='text-xs flex items-center gap-2'>
                <Users className='w-3 h-3' />
                Contacto usado {contactoRequerido ? <span className='text-red-600 font-semibold'>(obligatorio)</span> : <span className='text-slate-400'>(opcional)</span>}
              </Label>
              {contactoRequerido && (
                <span className='text-[10px] text-amber-700 flex items-center gap-1'>
                  <AlertCircle className='w-3 h-3' /> Requerido por el resultado/tipo seleccionado
                </span>
              )}
            </div>
            <Select
              value={form.contacto_idx === '' ? 'ninguno' : String(form.contacto_idx)}
              onValueChange={v => setForm(f => ({ ...f, contacto_idx: v === 'ninguno' ? '' : v }))}
            >
              <SelectTrigger data-testid='gestion-contacto' className='h-9'>
                <SelectValue placeholder={loadingDetalle ? 'Cargando contactos...' : 'Selecciona contacto'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ninguno'>Sin contacto</SelectItem>
                {contactos.map((c, i) => (
                  <SelectItem key={i} value={String(i)} data-testid={`gestion-contacto-opt-${i}`}>
                    {c.nombre || '(sin nombre)'} · {c.email || c.telefono || '—'} · {c.rol}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label className='text-xs'>Próxima acción</Label>
              <DebouncedInput
                value={form.proxima_accion}
                onChange={e => setForm(f => ({ ...f, proxima_accion: e.target.value }))}
                placeholder='Ej: Llamar en 3 días'
                data-testid='gestion-proxima-accion'
              />
            </div>
            <div>
              <Label className='text-xs'>Fecha próxima gestión</Label>
              <Input
                type='date'
                value={form.fecha_proxima_gestion}
                onChange={e => setForm(f => ({ ...f, fecha_proxima_gestion: e.target.value }))}
                data-testid='gestion-fecha-proxima'
                className='h-9'
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !form.resumen.trim()}
            data-testid='gestion-submit'
          >
            <CheckCircle className='w-4 h-4 mr-2' />
            {loading ? 'Guardando...' : 'Registrar Gestión'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default NuevaGestionModal;
